import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Building2, Plus, Search, Edit, Trash2, Save, ArrowLeft, Loader2 } from 'lucide-react';
import toast from "react-hot-toast";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActiveColor, 
  masterInactiveColor, 
  masterActionButtonsContainer, 
  masterTableCellstatus,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const DepartmentMaster = () => {
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false); // Default to list view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [formData, setFormData] = useState({
    deptCode: '',
    deptName: '',
    description: '',
    status: 'Active'
  });

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get('/api/departments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(res.data || []);
    } catch (err) {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...departments].sort((a, b) => {
      const idA = String(a.deptCode || a._id || '');
      const idB = String(b.deptCode || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const deptName = (item.deptName || '').toLowerCase();
      const deptCode = (item.deptCode || '').toLowerCase();
      return deptName.startsWith(q) || deptCode.startsWith(q);
    });
  }, [departments, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleAddNew = () => {
    setEditId(null);
    setFormData({ deptCode: '', deptName: '', description: '', status: 'Active' });
    setShowForm(true);
  };

  const handleEdit = (dept) => {
    setEditId(dept._id);
    setFormData({
      deptCode: dept.deptCode || '',
      deptName: dept.deptName || '',
      description: dept.description || '',
      status: dept.status || 'Active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/departments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      fetchDepartments();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.deptName.trim()) {
      toast.error("Department name is required");
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const payload = {
        deptName: formData.deptName.trim().toUpperCase(),
        description: formData.description,
        status: formData.status
      };

      if (editId) {
        await axios.put(`/api/departments/${editId}`, payload, config);
        toast.success("Updated successfully");
      } else {
        await axios.post('/api/departments', payload, config);
        toast.success("Saved successfully");
      }

      fetchDepartments();
      setShowForm(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to save/update department');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Restored Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Department Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={showForm ? masterPrimaryButton : masterAccentButton}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD DEPARTMENT</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by department name or code..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Table scrollable container for left-right movement */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      <th className={masterTableData}>Name</th>
                      <th className={masterTableDatastatus}>Status</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((d, index) => (
                        <tr key={d._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className={masterTableCell}>{d.deptName}</td>
                          <td className={masterTableCellstatus}>
                            <span className={d.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                              {d.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableCellstatus}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(d)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(d._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editId ? 'Edit Department Details' : 'Create New Department'}</h2>
              </div>
              <div className={masterFormGrid2}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Department Name</label>
                  <input 
                    required 
                    autoFocus
                    value={formData.deptName} 
                    onChange={(e) => setFormData({...formData, deptName: e.target.value})} 
                    className={masterInput} 
                    placeholder="Enter department name..."
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({...formData, status: e.target.value})} 
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={masterAccentButton}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editId ? 'UPDATE DEPARTMENT' : 'SAVE DEPARTMENT'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default DepartmentMaster;